import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { 
  Camera, 
  Plus, 
  Trash2, 
  Edit3, 
  Image as ImageIcon, 
  Crown,
  Upload,
  X,
  Check,
  Sparkles,
  Loader2,
  Link2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const StyleGallery = ({ token, membershipTier = "free" }) => {
  const [profilePicture, setProfilePicture] = useState("");
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryInfo, setGalleryInfo] = useState({
    limit: 5,
    remaining: 5,
    can_add_more: true
  });
  
  // File upload refs
  const profileFileRef = useRef(null);
  const galleryFileRef = useRef(null);
  
  // Dialog states
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  
  // Form states
  const [newProfileUrl, setNewProfileUrl] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newStyleName, setNewStyleName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'

  useEffect(() => {
    fetchGallery();
  }, [token]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/stylists/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfilePicture(response.data.profile_picture || "");
      setGallery(response.data.gallery || []);
      setGalleryInfo({
        limit: response.data.limit,
        remaining: response.data.remaining,
        can_add_more: response.data.can_add_more
      });
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle profile image file upload
  const handleProfileFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingProfile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'profile');

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;
      
      // Update profile picture on server
      await axios.put(
        `${API}/stylists/profile-picture`,
        { profile_picture_url: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfilePicture(imageUrl);
      setShowProfileDialog(false);
      toast.success("Profile picture updated!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingProfile(false);
      if (e.target) e.target.value = '';
    }
  };

  // Handle gallery image file upload
  const handleGalleryFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'gallery');

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;
      setNewImageUrl(imageUrl);
      toast.success("Image uploaded! Now add style details.");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingGallery(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleUpdateProfilePicture = async () => {
    if (!newProfileUrl.trim()) {
      toast.error("Please enter an image URL");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/stylists/profile-picture`,
        { profile_picture_url: newProfileUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfilePicture(newProfileUrl);
      setShowProfileDialog(false);
      setNewProfileUrl("");
      toast.success("Profile picture updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile picture");
    } finally {
      setSaving(false);
    }
  };

  const handleAddImage = async () => {
    if (!newImageUrl.trim()) {
      toast.error("Please enter an image URL");
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/stylists/gallery`,
        { 
          image_url: newImageUrl,
          style_name: newStyleName,
          description: newDescription
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGallery([...gallery, response.data.image]);
      setGalleryInfo(prev => ({
        ...prev,
        remaining: response.data.remaining_slots,
        can_add_more: membershipTier !== "free" || gallery.length + 1 < 5
      }));
      
      setShowAddDialog(false);
      setNewImageUrl("");
      setNewStyleName("");
      setNewDescription("");
      toast.success("Style image added to gallery!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add image");
    } finally {
      setSaving(false);
    }
  };

  const handleEditImage = async () => {
    if (!editingImage) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/stylists/gallery/${editingImage.image_id}?style_name=${encodeURIComponent(newStyleName)}&description=${encodeURIComponent(newDescription)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGallery(gallery.map(img => 
        img.image_id === editingImage.image_id 
          ? { ...img, style_name: newStyleName, description: newDescription }
          : img
      ));
      
      setShowEditDialog(false);
      setEditingImage(null);
      toast.success("Style details updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update image");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!confirm("Are you sure you want to delete this image?")) return;
    
    try {
      await axios.delete(
        `${API}/stylists/gallery/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGallery(gallery.filter(img => img.image_id !== imageId));
      setGalleryInfo(prev => ({
        ...prev,
        remaining: membershipTier === "free" ? prev.remaining + 1 : "unlimited",
        can_add_more: true
      }));
      
      toast.success("Image removed from gallery");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete image");
    }
  };

  const openEditDialog = (image) => {
    setEditingImage(image);
    setNewStyleName(image.style_name || "");
    setNewDescription(image.description || "");
    setShowEditDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const isPaidTier = membershipTier === "pro" || membershipTier === "elite";

  return (
    <div className="space-y-6">
      {/* Profile Picture Section */}
      <Card className="bg-stone-900/50 border-stone-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Camera className="w-5 h-5 text-amber-400" />
            Profile Picture
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-amber-500/30"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-stone-800 border-4 border-stone-700 flex items-center justify-center">
                  <Camera className="w-12 h-12 text-stone-600" />
                </div>
              )}
              <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
                <DialogTrigger asChild>
                  <Button
                    size="icon"
                    className="absolute bottom-0 right-0 rounded-full bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-stone-900 border-stone-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Update Profile Picture</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* File Upload Option - Primary */}
                    <div className="space-y-3">
                      <Label className="text-stone-300 font-medium">Upload from Device</Label>
                      <div 
                        onClick={() => !uploadingProfile && profileFileRef.current?.click()}
                        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                          uploadingProfile 
                            ? 'border-amber-500/50 bg-amber-500/10' 
                            : 'border-stone-600 hover:border-amber-500 hover:bg-amber-500/5'
                        }`}
                      >
                        {uploadingProfile ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-2" />
                            <span className="text-amber-400 text-sm">Uploading...</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="w-8 h-8 text-stone-400 mb-2" />
                            <span className="text-stone-300 font-medium">Click to upload photo</span>
                            <span className="text-stone-500 text-xs mt-1">JPG, PNG up to 5MB</span>
                          </div>
                        )}
                      </div>
                      <input
                        ref={profileFileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleProfileFileUpload}
                        className="hidden"
                      />
                    </div>

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-stone-700" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-stone-900 px-2 text-stone-500">or use URL</span>
                      </div>
                    </div>

                    {/* URL Option - Secondary */}
                    <div className="space-y-2">
                      <Label className="text-stone-400 text-sm flex items-center gap-2">
                        <Link2 className="w-3 h-3" />
                        Paste Image URL
                      </Label>
                      <Input
                        placeholder="https://example.com/your-photo.jpg"
                        value={newProfileUrl}
                        onChange={(e) => setNewProfileUrl(e.target.value)}
                        className="bg-stone-800 border-stone-700 text-white"
                      />
                    </div>
                    {newProfileUrl && (
                      <div className="flex justify-center">
                        <img
                          src={newProfileUrl}
                          alt="Preview"
                          className="w-24 h-24 rounded-full object-cover border-2 border-amber-500/30"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowProfileDialog(false);
                        setNewProfileUrl("");
                      }}
                      className="border-stone-700 text-stone-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleUpdateProfilePicture}
                      disabled={saving || !newProfileUrl.trim()}
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                    >
                      {saving ? "Saving..." : "Use URL"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div>
              <h3 className="text-white font-semibold">Your Profile Photo</h3>
              <p className="text-stone-400 text-sm mt-1">
                This appears on your profile and search results
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Style Gallery Section */}
      <Card className="bg-stone-900/50 border-stone-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-amber-400" />
              Style Gallery
            </CardTitle>
            <p className="text-stone-400 text-sm mt-1">
              Showcase your best work to attract clients
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Gallery limit badge */}
            {membershipTier === "free" ? (
              <Badge variant="outline" className="border-stone-600 text-stone-400">
                {gallery.length}/5 images
              </Badge>
            ) : (
              <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black">
                <Crown className="w-3 h-3 mr-1" />
                Unlimited
              </Badge>
            )}
            
            {/* Add Image Button */}
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) {
                setNewImageUrl("");
                setNewStyleName("");
                setNewDescription("");
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  disabled={!galleryInfo.can_add_more}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Style
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-stone-900 border-stone-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Add Style Image</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* File Upload Option - Primary */}
                  {!newImageUrl && (
                    <div className="space-y-3">
                      <Label className="text-stone-300 font-medium">Upload Photo</Label>
                      <div 
                        onClick={() => !uploadingGallery && galleryFileRef.current?.click()}
                        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                          uploadingGallery 
                            ? 'border-amber-500/50 bg-amber-500/10' 
                            : 'border-stone-600 hover:border-amber-500 hover:bg-amber-500/5'
                        }`}
                      >
                        {uploadingGallery ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="w-10 h-10 text-amber-500 animate-spin mb-2" />
                            <span className="text-amber-400">Uploading...</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="w-10 h-10 text-stone-400 mb-2" />
                            <span className="text-stone-300 font-medium">Click to upload photo</span>
                            <span className="text-stone-500 text-sm mt-1">JPG, PNG up to 5MB</span>
                          </div>
                        )}
                      </div>
                      <input
                        ref={galleryFileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleGalleryFileUpload}
                        className="hidden"
                      />
                      
                      {/* Divider */}
                      <div className="relative pt-2">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-stone-700" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-stone-900 px-2 text-stone-500">or paste URL</span>
                        </div>
                      </div>
                      
                      {/* URL Input */}
                      <div className="space-y-2 pt-2">
                        <Label className="text-stone-400 text-sm flex items-center gap-2">
                          <Link2 className="w-3 h-3" />
                          Image URL
                        </Label>
                        <Input
                          placeholder="https://example.com/style-photo.jpg"
                          value={newImageUrl}
                          onChange={(e) => setNewImageUrl(e.target.value)}
                          className="bg-stone-800 border-stone-700 text-white"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Show uploaded image preview and style details */}
                  {newImageUrl && (
                    <>
                      <div className="relative">
                        <img
                          src={newImageUrl}
                          alt="Preview"
                          className="w-full max-h-48 rounded-lg object-cover border border-stone-700"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="absolute top-2 right-2 h-8 w-8 bg-black/50 hover:bg-black/70 text-white"
                          onClick={() => setNewImageUrl("")}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Badge className="absolute bottom-2 left-2 bg-green-500 text-white">
                          <Check className="w-3 h-3 mr-1" />
                          Image Ready
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-stone-300">Style Name (optional)</Label>
                        <Input
                          placeholder="e.g., Classic Fade, Box Braids, Balayage"
                          value={newStyleName}
                          onChange={(e) => setNewStyleName(e.target.value)}
                          className="bg-stone-800 border-stone-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-stone-300">Description (optional)</Label>
                        <Input
                          placeholder="Brief description of this style"
                          value={newDescription}
                          onChange={(e) => setNewDescription(e.target.value)}
                          className="bg-stone-800 border-stone-700 text-white"
                        />
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddDialog(false);
                      setNewImageUrl("");
                      setNewStyleName("");
                      setNewDescription("");
                    }}
                    className="border-stone-700 text-stone-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddImage}
                    disabled={saving || !newImageUrl.trim()}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    {saving ? "Adding..." : "Add to Gallery"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Upgrade prompt for free users at limit */}
          {membershipTier === "free" && gallery.length >= 5 && (
            <div className="mb-6 p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8 text-amber-400" />
                <div>
                  <h4 className="text-amber-400 font-semibold">Upgrade for Unlimited Gallery</h4>
                  <p className="text-stone-400 text-sm">
                    Pro and Elite members can upload unlimited style images to showcase their work.
                  </p>
                </div>
                <Button
                  className="ml-auto bg-gradient-to-r from-amber-500 to-yellow-500 text-black"
                  onClick={() => window.location.href = '/membership'}
                >
                  Upgrade Now
                </Button>
              </div>
            </div>
          )}

          {/* Gallery Grid */}
          {gallery.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-stone-700 rounded-lg">
              <ImageIcon className="w-12 h-12 mx-auto text-stone-600 mb-3" />
              <h3 className="text-stone-400 font-medium mb-1">No styles yet</h3>
              <p className="text-stone-500 text-sm mb-4">
                Add photos of your best work to attract more clients
              </p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Style
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((image) => (
                <div
                  key={image.image_id}
                  className="group relative aspect-square rounded-lg overflow-hidden border border-stone-700 hover:border-amber-500/50 transition-all"
                >
                  <img
                    src={image.image_url}
                    alt={image.style_name || "Style"}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-white hover:bg-white/20"
                        onClick={() => openEditDialog(image)}
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-red-400 hover:bg-red-500/20"
                        onClick={() => handleDeleteImage(image.image_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {image.style_name && (
                      <div>
                        <p className="text-white font-medium text-sm truncate">
                          {image.style_name}
                        </p>
                        {image.description && (
                          <p className="text-stone-400 text-xs truncate">
                            {image.description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Style name badge (always visible) */}
                  {image.style_name && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 group-hover:opacity-0 transition-opacity">
                      <p className="text-white text-sm font-medium truncate">
                        {image.style_name}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Image Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-stone-900 border-stone-700">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Style Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingImage && (
              <div className="flex justify-center mb-4">
                <img
                  src={editingImage.image_url}
                  alt="Preview"
                  className="max-h-32 rounded-lg object-cover border border-stone-700"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-stone-300">Style Name</Label>
              <Input
                placeholder="e.g., Classic Fade, Box Braids"
                value={newStyleName}
                onChange={(e) => setNewStyleName(e.target.value)}
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-300">Description</Label>
              <Input
                placeholder="Brief description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="border-stone-700 text-stone-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditImage}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StyleGallery;
